import { useEffect, useRef } from 'react';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const SAVE_DEBOUNCE_MS = 500;

type UsePageNoteAutosaveArgs = Readonly<{
  /** The server's note at first render. Later server values come through `resetBaseline`. */
  savedNote: string;
  /** Persists the note. Resolves false when the write failed. */
  onPersist: (html: string) => Promise<boolean>;
}>;

/**
 * Autosave for the inline page note: debounced while typing, flushed on blur. Writes run one at a
 * time and each reads the draft at its turn, so anything typed mid-save is carried into the next.
 */
export const usePageNoteAutosave = ({ savedNote, onPersist }: UsePageNoteAutosaveArgs) => {
  const draftRef = useRef(savedNote);
  // Only a successful write or `resetBaseline` moves this — never a local draft.
  const lastSavedRef = useRef(savedNote);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const { value: debouncedNote, setValue: setDebouncedNote } = useDebouncedValue<string>(SAVE_DEBOUNCE_MS, savedNote);

  const persistDraft = useEventCallback(async () => {
    const html = draftRef.current;
    if (html === lastSavedRef.current) {
      return;
    }
    const previousSaved = lastSavedRef.current;
    lastSavedRef.current = html;
    const didSave = await onPersist(html);
    // A failed write stays dirty for the next keystroke or blur. Retrying here would never stop.
    if (!didSave) {
      lastSavedRef.current = previousSaved;
    }
  });

  const save = useEventCallback((): Promise<void> => {
    const settled = (inFlightRef.current ?? Promise.resolve()).then(persistDraft).finally(() => {
      if (inFlightRef.current === settled) {
        inFlightRef.current = null;
      }
    });
    inFlightRef.current = settled;
    return settled;
  });

  const handleChange = useEventCallback((html: string) => {
    draftRef.current = html;
    setDebouncedNote(html);
  });

  /** Saves now, skipping the debounce. Resolves once the write has settled. */
  const flush = useEventCallback(() => save());

  /** Adopts a note that came from the server, so it is not written back. */
  const resetBaseline = useEventCallback((html: string) => {
    draftRef.current = html;
    lastSavedRef.current = html;
  });

  /** True when nothing is pending, so an incoming server value is safe to adopt. */
  const isSettled = useEventCallback(() => inFlightRef.current === null && draftRef.current === lastSavedRef.current);

  useEffect(() => {
    if (debouncedNote !== undefined) {
      void save();
    }
  }, [debouncedNote, save]);

  return { handleChange, flush, resetBaseline, isSettled };
};
